import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Palette, Upload, Save, Building2, RefreshCw } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Label } from '../../components/ui/Label'
import { Badge } from '../../components/ui/Badge'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { adminApi, AdminOrg } from '../../services/api/admin'
import { brandingApi } from '../../services/api/branding'
import { Branding } from '../../types/branding'
import { usePermissionContext } from '../../contexts/PermissionContext'
import toast from 'react-hot-toast'

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/

const brandingSchema = z.object({
  slug: z.string().min(1, 'Slug is required').max(64, 'Slug must be 64 characters or fewer')
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, digits, and hyphens only'),
  primaryColor: z.string().regex(HEX_REGEX, 'Must be a valid hex color (e.g. #1a2b3c)')
    .or(z.literal('')),
  accentColor: z.string().regex(HEX_REGEX, 'Must be a valid hex color (e.g. #1a2b3c)')
    .or(z.literal('')),
})

type BrandingFormValues = z.infer<typeof brandingSchema>

interface UploadState {
  logo: 'idle' | 'uploading' | 'done' | 'error'
  logoDark: 'idle' | 'uploading' | 'done' | 'error'
  favicon: 'idle' | 'uploading' | 'done' | 'error'
}

export const AdminBrandingPage: React.FC = () => {
  const { orgId: urlOrgId } = useParams<{ orgId: string }>()
  const { isPlatformAdmin } = usePermissionContext()
  const [orgs, setOrgs] = useState<AdminOrg[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>(urlOrgId ?? '')
  const [branding, setBranding] = useState<Branding | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadState, setUploadState] = useState<UploadState>({
    logo: 'idle',
    logoDark: 'idle',
    favicon: 'idle',
  })

  const logoInputRef = useRef<HTMLInputElement>(null)
  const logoDarkInputRef = useRef<HTMLInputElement>(null)
  const faviconInputRef = useRef<HTMLInputElement>(null)

  const uploadRefs = {
    logo: logoInputRef,
    logoDark: logoDarkInputRef,
    favicon: faviconInputRef,
  }

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<BrandingFormValues>({
    resolver: zodResolver(brandingSchema),
    defaultValues: { slug: '', primaryColor: '', accentColor: '' },
  })

  const watchedPrimary = watch('primaryColor')
  const watchedAccent = watch('accentColor')

  useEffect(() => {
    if (urlOrgId) {
      setSelectedOrgId(urlOrgId)
    }
    if (isPlatformAdmin) {
      loadOrgs()
    } else {
      setLoading(false)
    }
  }, [urlOrgId, isPlatformAdmin])

  const loadOrgs = async () => {
    try {
      const data = await adminApi.getOrgs()
      setOrgs(data)
      if (data.length > 0 && !selectedOrgId) {
        setSelectedOrgId(data[0].id)
      }
    } catch {
      toast.error('Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }

  const loadBranding = useCallback(async (orgId: string) => {
    if (!orgId) return
    setLoading(true)
    try {
      const data = await brandingApi.getForOrg(orgId)
      setBranding(data)
      reset({
        slug: data.slug || '',
        primaryColor: data.primaryColor || '',
        accentColor: data.accentColor || '',
      })
    } catch {
      toast.error('Failed to load branding for this organization')
    } finally {
      setLoading(false)
    }
  }, [reset])

  useEffect(() => {
    if (selectedOrgId) {
      loadBranding(selectedOrgId)
    }
  }, [selectedOrgId, loadBranding])

  const onSubmit = async (values: BrandingFormValues) => {
    if (!selectedOrgId) return
    setSaving(true)
    try {
      await brandingApi.update(selectedOrgId, {
        slug: values.slug,
        primaryColor: values.primaryColor || null,
        accentColor: values.accentColor || null,
      })
      toast.success('Branding saved')
    } catch {
      toast.error('Failed to save branding')
    } finally {
      setSaving(false)
    }
  }

  const handleFileUpload = async (
    kind: 'logo' | 'logoDark' | 'favicon',
    file: File
  ) => {
    if (!selectedOrgId) return
    setUploadState((prev) => ({ ...prev, [kind]: 'uploading' }))
    try {
      if (kind === 'logo') {
        await brandingApi.uploadLogo(selectedOrgId, file)
      } else if (kind === 'logoDark') {
        await brandingApi.uploadLogoDark(selectedOrgId, file)
      } else {
        await brandingApi.uploadFavicon(selectedOrgId, file)
      }
      setUploadState((prev) => ({ ...prev, [kind]: 'done' }))
      toast.success(`${kind === 'logoDark' ? 'Dark logo' : kind === 'favicon' ? 'Favicon' : 'Logo'} uploaded`)
      await loadBranding(selectedOrgId)
    } catch {
      setUploadState((prev) => ({ ...prev, [kind]: 'error' }))
      toast.error('Upload failed')
    }
  }

  const onFileChange = (kind: 'logo' | 'logoDark' | 'favicon') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) handleFileUpload(kind, file)
    }

  const previewPrimary = (HEX_REGEX.test(watchedPrimary) ? watchedPrimary : null)
    || branding?.primaryColor
    || '#69adba'
  const previewAccent = (HEX_REGEX.test(watchedAccent) ? watchedAccent : null)
    || branding?.accentColor
    || '#69adba'

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId)
  const displayOrgName = selectedOrg?.name ?? branding?.orgName ?? selectedOrgId
  const formReady = !!(selectedOrgId && !loading && branding)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Brand Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure per-organization branding for white-label portal views.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {orgs.map((org) => (
          <Button
            key={org.id}
            variant={selectedOrgId === org.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedOrgId(org.id)}
          >
            <Building2 className="h-4 w-4 mr-1.5" aria-hidden="true" />
            {org.name}
          </Button>
        ))}
      </div>

      {formReady && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5 text-primary" aria-hidden="true" />
                  Organization Details
                </CardTitle>
                <CardDescription>
                  Slug and brand colors for {displayOrgName}.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="org-name">Organization name</Label>
                  <Input
                    id="org-name"
                    value={displayOrgName}
                    disabled
                    aria-label="Organization name (read-only)"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="slug">URL slug</Label>
                  <Input
                    id="slug"
                    placeholder="westcor"
                    aria-invalid={!!errors.slug}
                    aria-describedby={errors.slug ? 'slug-error' : undefined}
                    {...register('slug')}
                  />
                  {errors.slug && (
                    <p id="slug-error" role="alert" className="text-xs text-destructive">
                      {errors.slug.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="primaryColor">Primary color</Label>
                    <Controller
                      name="primaryColor"
                      control={control}
                      render={({ field }) => (
                        <div className="flex items-center gap-2">
                          <Input
                            type="color"
                            id="primaryColor-picker"
                            aria-label="Primary color picker"
                            value={HEX_REGEX.test(field.value) ? field.value : '#69adba'}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              setValue('primaryColor', e.target.value, { shouldValidate: true })
                            }}
                            className="h-9 w-12 cursor-pointer p-0.5"
                          />
                          <Input
                            id="primaryColor"
                            placeholder="#69adba"
                            aria-invalid={!!errors.primaryColor}
                            aria-describedby={errors.primaryColor ? 'primary-color-error' : undefined}
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                            }}
                          />
                        </div>
                      )}
                    />
                    {errors.primaryColor && (
                      <p id="primary-color-error" role="alert" className="text-xs text-destructive">
                        {errors.primaryColor.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="accentColor">Accent color</Label>
                    <Controller
                      name="accentColor"
                      control={control}
                      render={({ field }) => (
                        <div className="flex items-center gap-2">
                          <Input
                            type="color"
                            id="accentColor-picker"
                            aria-label="Accent color picker"
                            value={HEX_REGEX.test(field.value) ? field.value : '#69adba'}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                              setValue('accentColor', e.target.value, { shouldValidate: true })
                            }}
                            className="h-9 w-12 cursor-pointer p-0.5"
                          />
                          <Input
                            id="accentColor"
                            placeholder="#69adba"
                            aria-invalid={!!errors.accentColor}
                            aria-describedby={errors.accentColor ? 'accent-color-error' : undefined}
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.value)
                            }}
                          />
                        </div>
                      )}
                    />
                    {errors.accentColor && (
                      <p id="accent-color-error" role="alert" className="text-xs text-destructive">
                        {errors.accentColor.message}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-primary" aria-hidden="true" />
                  Logo & Favicon
                </CardTitle>
                <CardDescription>
                  Upload assets to R2. Logos are served publicly.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {([
                  { kind: 'logo' as const, label: 'Light logo', hint: 'PNG or SVG, shown on light backgrounds' },
                  { kind: 'logoDark' as const, label: 'Dark logo', hint: 'PNG or SVG, shown on dark backgrounds' },
                  { kind: 'favicon' as const, label: 'Favicon', hint: 'ICO or PNG, 32×32 px minimum' },
                ] as const).map(({ kind, label, hint }) => {
                  const state = uploadState[kind]
                  return (
                    <div key={kind} className="flex items-start gap-4">
                      <div className="flex-1 space-y-1">
                        <Label>{label}</Label>
                        <p className="text-xs text-muted-foreground">{hint}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {state === 'uploading' && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Uploading" />}
                        {state === 'done' && <Badge variant="success">Uploaded</Badge>}
                        {state === 'error' && <Badge variant="destructive">Failed</Badge>}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => uploadRefs[kind].current?.click()}
                          disabled={state === 'uploading'}
                        >
                          <Upload className="h-4 w-4 mr-1.5" aria-hidden="true" />
                          Upload
                        </Button>
                        <Input
                          ref={uploadRefs[kind]}
                          type="file"
                          accept="image/*,.ico"
                          className="sr-only"
                          aria-label={`Upload ${label}`}
                          onChange={onFileChange(kind)}
                          tabIndex={-1}
                        />
                      </div>
                    </div>
                  )
                })}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={saving}
              >
                <Save className="h-4 w-4 mr-2" aria-hidden="true" />
                {saving ? 'Saving...' : 'Save branding'}
              </Button>
            </div>
          </form>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Live preview</CardTitle>
                <CardDescription>Approximate rendering with current color selections.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className="rounded-2xl border p-4 space-y-3"
                  style={{ borderColor: previewPrimary }}
                >
                  <div
                    className="h-2 rounded-full"
                    style={{ background: previewPrimary }}
                    aria-hidden="true"
                  />
                  <p className="text-sm font-semibold text-foreground">Sample card title</p>
                  <p className="text-xs text-muted-foreground">
                    This card uses the selected primary color for its border and progress bar.
                  </p>
                  <button
                    type="button"
                    aria-label="Sample primary button preview"
                    className="w-full py-2 rounded-lg text-sm font-semibold text-white"
                    style={{ backgroundColor: previewPrimary }}
                  >
                    Primary action
                  </button>
                  <button
                    type="button"
                    aria-label="Sample accent button preview"
                    className="w-full py-2 rounded-lg text-sm font-medium"
                    style={{ backgroundColor: previewAccent, color: '#1a1a1a' }}
                  >
                    Accent action
                  </button>
                </div>

                <div className="rounded-xl p-3 border border-border space-y-2">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                    Sidebar item
                  </p>
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
                    style={{ backgroundColor: `${previewPrimary}22`, color: previewPrimary }}
                    aria-hidden="true"
                  >
                    <Building2 className="h-4 w-4" />
                    Dashboard
                  </div>
                </div>

                {branding?.logoUrl && (
                  <div className="rounded-xl border border-border p-3">
                    <p className="text-xs text-muted-foreground font-medium mb-2">Current light logo</p>
                    <img
                      src={branding.logoUrl}
                      alt="Organization light logo"
                      className="h-8 w-auto object-contain"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {!formReady && !loading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <p className="text-muted-foreground text-sm">Select an organization above to manage its branding.</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
